import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge, Drawer, Spin, Tooltip } from 'antd';
import { MailOutlined, AppstoreOutlined } from '@ant-design/icons';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const InboxPage = lazy(() => import('../pages/Inbox'));
const ModuleCategoryDrawer = lazy(() => import('../pages/ERP/ModuleCategoryDrawer'));

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showModules, setShowModules] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 監聽未讀訊息數量 (Firestore real-time)
  useEffect(() => {
    if (!user?.userId) return;

    const q = query(
      collection(db, 'inbox', user.userId, 'messages'),
      where('isRead', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    }, () => {
      setUnreadCount(0); // Firestore error fallback
    });

    return () => unsubscribe();
  }, [user?.userId]);

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center ml-64">
            <img src="/logo.png" alt="Pepwing" style={{ height: 44 }} />
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Module Category */}
            <Tooltip title="模組分類">
              <button
                onClick={() => setShowModules(true)}
                className="p-2 text-gray-400 hover:text-gray-600 relative"
              >
                <AppstoreOutlined style={{ fontSize: '20px' }} />
              </button>
            </Tooltip>

            {/* Inbox */}
            <Badge count={unreadCount} offset={[-5, 5]}>
              <button
                onClick={() => setShowInbox(true)}
                className="p-2 text-gray-400 hover:text-gray-600 relative"
              >
                <MailOutlined style={{ fontSize: '20px' }} />
              </button>
            </Badge>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-3 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    {user?.userName?.charAt(0) || 'U'}
                  </div>
                  <div className="text-left">
                    <div className="font-medium">{user?.userName || '使用者'}</div>
                    <div className="text-xs text-gray-500">{user?.deptName || '部門'}</div>
                  </div>
                </div>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowDropdown(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      <div className="font-medium">{user?.userName}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                    
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/user/profile');
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        個人資料
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/user/change-password');
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        修改密碼
                      </div>
                    </button>
                    
                    <div className="border-t"></div>
                    
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        登出
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 模組分類 Drawer */}
      <Drawer
        title="模組分類"
        placement="right"
        open={showModules}
        onClose={() => setShowModules(false)}
        width={520}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <Suspense fallback={<div className="flex justify-center py-12"><Spin size="large" /></div>}>
          <ModuleCategoryDrawer onClose={() => setShowModules(false)} />
        </Suspense>
      </Drawer>

      {/* 收件夾 Drawer（Lazy 載入，不中斷工作頁面） */}
      <Drawer
        title="收件夾"
        placement="right"
        open={showInbox}
        onClose={() => setShowInbox(false)}
        width={720}
        styles={{ body: { padding: 0, background: '#f9fafb' } }}
        destroyOnClose
      >
        <Suspense fallback={<div className="flex justify-center py-12"><Spin size="large" /></div>}>
          <InboxPage />
        </Suspense>
      </Drawer>
    </header>
  );
};

export default Header;